import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react"
import { useParams, Link } from "react-router-dom"
import { AuthContext } from "../App"
import {
  Save,
  Trash2,
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  FilePlus,
  Download,
  ShoppingCart,
  RefreshCw,
  Building2,
  Search,
  X,
  Eye,
  Package,
  Image as ImageIcon,
  ZoomIn,
  Plus
} from "lucide-react"
import "./DetallePedido.css"
import {
  clienteUsaSubfamilia,
  categoriaProducto,
  compatibilidadAlAnadirProducto,
} from "../utils/subfamiliaPedido"
import "./NuevoPedido.css"
import { useTheme } from "../contexts/ThemeContext"

const API_URL = process.env.REACT_APP_API_URL;

// ── Clasificación de familias / tipo de pedido (misma lógica que NuevoPedido) ──
// Solo aplica a clientes con la lógica de tipos (Egos Balmes / cliente de prueba).
const debeVerFiltroFamilia = (clienteId, clinicaMatriz) =>
  clienteId == 999999 || clienteId == 131 || clinicaMatriz == 999999 || clinicaMatriz == 131;

// Normaliza familia para comparar: mayúsculas y SIN diacríticos.
const normalizarFamilia = (familia) =>
  String(familia || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Mapeo EXPLÍCITO familia real -> categoría de producto. 'VESTUARIO' es el
// valor real en producción (proveedor MELAN VESTUARI LABORAL); 'UNIFORMES' se
// acepta como alias por si el dato se normaliza en el futuro.
const FAMILIAS_ACEPTADAS_POR_FILTRO = {
  OFICINA:     ["OFICINA"],
  LIMPIEZA:    ["LIMPIEZA"],
  UNIFORMES:   ["VESTUARIO", "UNIFORMES"],
  INFORMATICA: ["INFORMATICA"],
};

const clasificarFamilia = (familiaProducto) => {
  if (!familiaProducto) return "material-sanitario";
  const familia = normalizarFamilia(familiaProducto);
  for (const [clave, alias] of Object.entries(FAMILIAS_ACEPTADAS_POR_FILTRO)) {
    // Exacta, no `includes`: 'MATERIAL DE OFICINA' no debe pasar por 'oficina'.
    if (alias.includes(familia)) return clave;
  }
  return "material-sanitario";
};

// Colapsa la familia en las categorías madre del pedido:
//   - "sanitario": material sanitario  - "oficina_limpieza": oficina + limpieza.
// Uniformes e Informática son COMPATIBLES CON TODO y NO se colapsan en
// "oficina_limpieza" (esa categoría es una regla de bloqueo que fuerza el tipo
// a Cirugía); se tratan como "sanitario", que es neutro.
const getCategoriaPedido = (familiaProducto) => {
  const clasificada = clasificarFamilia(familiaProducto);
  return (clasificada === "OFICINA" || clasificada === "LIMPIEZA")
    ? "oficina_limpieza"
    : "sanitario";
};

// Etiqueta legible del tipo de producto.
const etiquetaTipoProducto = (familiaProducto) => {
  switch (clasificarFamilia(familiaProducto)) {
    case "OFICINA": return "Oficina";
    case "LIMPIEZA": return "Limpieza";
    case "UNIFORMES": return "Uniformes";
    case "INFORMATICA": return "Informática";
    default: return "Sanitario";
  }
};

// Etiqueta del tipo de pedido (medicina estética / cirugía plástica).
const etiquetaTipoPedido = (tipo) =>
  tipo === "medicina_estetica" ? "Medicina Estética"
  : tipo === "cirugia_plastica" ? "Cirugía Plástica"
  : null;

// Funciones para manejar sessionStorage de líneas temporales (adaptado de NuevoPedido.js)
const getSessionKey = (pedidoId) => `pedido_modificacion_${pedidoId}`

const getLineasTemporales = (pedidoId) => {
  if (!pedidoId) return {}
  try {
    const data = sessionStorage.getItem(getSessionKey(pedidoId))
    return data ? JSON.parse(data) : {}
  } catch {
    return {}
  }
}

const setLineaTemporal = (pedidoId, productId, lineaData) => {
  if (!pedidoId) return
  try {
    const lineasTemporales = getLineasTemporales(pedidoId)
    if (lineaData.cantidad > 0) {
      lineasTemporales[productId] = lineaData
    } else {
      delete lineasTemporales[productId]
    }
    sessionStorage.setItem(getSessionKey(pedidoId), JSON.stringify(lineasTemporales))
  } catch (error) {
    console.error("Error guardando en sessionStorage:", error)
  }
}

const removeLineaTemporal = (pedidoId, productId) => {
  if (!pedidoId) return
  try {
    const lineasTemporales = getLineasTemporales(pedidoId)
    delete lineasTemporales[productId]
    sessionStorage.setItem(getSessionKey(pedidoId), JSON.stringify(lineasTemporales))
  } catch (error) {
    console.error("Error removiendo de sessionStorage:", error)
  }
}

const clearLineasTemporales = (pedidoId) => {
  if (!pedidoId) return
  try {
    sessionStorage.removeItem(getSessionKey(pedidoId))
  } catch (error) {
    console.error("Error limpiando sessionStorage:", error)
  }
}

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Componente para mostrar referencia truncada con tooltip
const TruncatedReference = React.memo(({ referencia, maxLength = 10 }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  
  if (!referencia || referencia.length <= maxLength) {
    return <span>{referencia || "-"}</span>
  }

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="cursor-help">
        {referencia.substring(0, maxLength)}...
      </span>
      {showTooltip && (
        <div className="absolute z-50 bg-gray-900 text-white text-xs rounded py-1 px-2 -top-8 left-0 whitespace-nowrap shadow-lg">
          {referencia}
          <div className="absolute top-full left-2 w-0 h-0 border-l-2 border-l-transparent border-r-2 border-r-transparent border-t-2 border-t-gray-900"></div>
        </div>
      )}
    </div>
  )
})

// Componente para mostrar imagen del producto
const ProductImage = React.memo(({ imagen, nombre, onImageClick }) => {
  const [imageError, setImageError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  if (!imagen || imageError) {
    return null
  }

  return (
    <div className="w-8 h-8 relative">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded flex items-center justify-center">
          <ImageIcon size={12} className="text-gray-400" />
        </div>
      )}
      <img
        src={imagen}
        alt={nombre}
        className={`w-full h-full object-cover rounded cursor-pointer hover:opacity-80 transition-opacity ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setImageError(true)
          setIsLoading(false)
        }}
        onClick={() => onImageClick(imagen, nombre)}
        title="Click para ver en grande"
      />
    </div>
  )
})

// Modal para mostrar imagen en grande
const ImageModal = React.memo(({ isOpen, imageSrc, imageAlt, onClose }) => {
  const modalRef = useRef(null)

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'auto'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div 
        ref={modalRef}
        className="relative max-w-4xl max-h-full bg-white rounded-lg shadow-xl overflow-hidden"
      >
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>
    </div>
  )
})

const DetallePedido = () => {
  const { pedidoId } = useParams()
  const { user } = useContext(AuthContext)
  const { theme } = useTheme()
  const token = localStorage.getItem("token")

  // Estados para datos
  const [pedido, setPedido] = useState(null)
  const [articulos, setArticulos] = useState([])
  const [productosDisponibles, setProductosDisponibles] = useState([])
  
  // Estados para control de carga
  const [loading, setLoading] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [error, setError] = useState(null)
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" })

  // Estados para formularios
  const [articulosRecibidos, setArticulosRecibidos] = useState([])
  const [productoSeleccionado, setProductoSeleccionado] = useState("")
  const [cantidadNuevo, setCantidadNuevo] = useState(1)
  
  // Estados para los valores de edición de artículos
  const [articulosEditados, setArticulosEditados] = useState({})

  // Estados para la funcionalidad mejorada de selección de productos
  const [busquedaProducto, setBusquedaProducto] = useState("")
  const [modalImagenVisible, setModalImagenVisible] = useState(false)
  const [imagenSeleccionada, setImagenSeleccionada] = useState({ src: "", alt: "" })
  const [productosCompletos, setProductosCompletos] = useState([])
  const [loadingProductos, setLoadingProductos] = useState(false)
  const [cantidadesTemporales, setCantidadesTemporales] = useState({})
  
  // Estados para gestión de líneas temporales (como NuevoPedido.js)
  const [lineasTemporales, setLineasTemporales] = useState({})
  const [articulosModificados, setArticulosModificados] = useState({})
  const [articulosEliminados, setArticulosEliminados] = useState(new Set())
  const [cambiosPendientes, setCambiosPendientes] = useState(false)
  
  // Debounce para la búsqueda
  const busquedaDebounced = useDebounce(busquedaProducto, 300)

  // Función para determinar si el usuario está viendo un pedido de clínica matriz
  const isViendoPedidoClinicaMatriz = () => {
    return user?.clinica_matriz > 0 && pedido?.cliente !== user?.clinica_matriz
  }

  // Función para determinar si el usuario puede agregar/eliminar artículos
  const puedeModificarArticulos = () => {
    // Administradores siempre pueden (excepto permisos 4, 5, 3)
    if (![4, 5, 3].includes(user?.permisos)) {
      return true
    }
    // Usuario de clínica matriz puede solo cuando el estado del pedido es 6
    if (user?.clinica_matriz > 0 && pedido?.estado == 6) {
      return true
    }
    return false
  }

  // Función para determinar si es usuario de clínica matriz (y por tanto tiene restricciones)
  const esUsuarioClinicaMatriz = () => {
    return user?.clinica_matriz > 0 && pedido?.estado == 6
  }

  // Función para determinar la ruta de regreso según el tipo de usuario
  const getBackRoute = () => {
    // Si es cliente (permisos 4 o 5), va a gestion-clientes
    if ([4, 5, 3].includes(user?.permisos)) {
      return "/gestion-clientes"
    }
    // Si es admin u otros permisos, va a gestion-pedidos
    return "/gestion-pedidos"
  }

  // Función para obtener el texto del botón según el tipo de usuario
  const getBackButtonText = () => {
    if ([4, 5, 3].includes(user?.permisos)) {
      return "Volver a Mi Gestión"
    }
    return "Volver a Pedidos"
  }

  // Función para formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  // Funciones para la nueva funcionalidad de selección de productos
  const handleImageClick = (src, alt) => {
    setImagenSeleccionada({ src, alt })
    setModalImagenVisible(true)
  }

  const handleCloseImageModal = () => {
    setModalImagenVisible(false)
    setImagenSeleccionada({ src: "", alt: "" })
  }

  // Cargar productos completos del proveedor
  const cargarProductosCompletos = async (proveedorId, clienteId) => {
    if (!proveedorId || !clienteId) return

    try {
      setLoadingProductos(true)
      // MISMA fuente que NuevoPedido: solo los productos y PRECIOS que tiene
      // asignados el cliente (precios.cmatriz), deduplicados por p.id.
      const response = await fetch(
        `${API_URL}/api/productos/buscar?query=&proveedor_id=${proveedorId}&cliente_id=${clienteId}&limit=5000`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.ok) {
        const data = await response.json()
        // Una misma referencia no puede duplicarse: nos quedamos con la primera.
        // (La tabla producto arrastra refs repetidas con distinto id.)
        const vistos = new Set()
        const unicos = []
        for (const p of (data.productos || [])) {
          const ref = (p.ref_proveedor || "").trim()
          const key = ref ? ref.toUpperCase() : `__id_${p.id}`
          if (vistos.has(key)) continue
          vistos.add(key)
          unicos.push(p)
        }
        setProductosCompletos(unicos)
      }
    } catch (error) {
      console.error("Error cargando productos:", error)
    } finally {
      setLoadingProductos(false)
    }
  }

  // Filtrar productos según búsqueda y disponibilidad
  // ¿Aplica la lógica de tipos (medicina estética / cirugía plástica)?
  const aplicaTipos = !!pedido && debeVerFiltroFamilia(pedido.cliente, pedido.clinica_matriz)

  // En la REVISIÓN del pedido se muestran TODOS los productos de TODAS las
  // categorías (cirugía, medicina, oficina, limpieza), tanto al navegar la lista
  // como al buscar. Que un pedido sea de una sola categoría se sigue controlando
  // al AÑADIR un producto (validación en front + backend 409), no ocultándolos
  // del listado: así el revisor puede encontrar cualquier producto antes de
  // confirmar.
  const productosFiltrados = productosCompletos.filter(producto =>
    busquedaDebounced === "" ||
    producto.nombre?.toLowerCase().includes(busquedaDebounced.toLowerCase()) ||
    producto.ref_proveedor?.toLowerCase().includes(busquedaDebounced.toLowerCase())
  )

  // Funciones para manejar líneas temporales (mejorado basado en NuevoPedido.js)
  const handleCantidadChange = useCallback((productId, cantidadInput) => {
    if (!pedidoId) return

    // Actualizar cantidad temporal sin agregar automáticamente
    setCantidadesTemporales(prev => ({
      ...prev,
      [productId]: cantidadInput
    }))
  }, [pedidoId])

  // Función para confirmar agregación/modificación (onBlur o Enter)
  const confirmarCantidad = useCallback((producto, cantidadInput) => {
    const cantidadStr = cantidadInput !== null && cantidadInput !== undefined ? cantidadInput.toString().trim() : ""
    
    // Si está vacío o es 0, eliminar
    if (!cantidadStr || cantidadStr === "" || cantidadStr === "0") {
      eliminarProducto(producto.id)
      return
    }

    // Parsear cantidad
    let cantidadNum
    try {
      const cantidadNormalizada = cantidadStr.replace(',', '.')
      cantidadNum = parseFloat(cantidadNormalizada)
      
      if (isNaN(cantidadNum) || cantidadNum <= 0) {
        console.warn(`Cantidad inválida: "${cantidadStr}"`)
        // Resetear a cantidad anterior o 1
        setCantidadesTemporales(prev => ({
          ...prev,
          [producto.id]: 1
        }))
        return
      }
    } catch (error) {
      console.error(`Error al parsear cantidad "${cantidadStr}":`, error)
      return
    }

    // ─── Validación cirugía/medicina (solo clientes 131/999999) ──────────
    // Si vamos a añadir/incrementar (cantidad > 0) un producto con subfamilia
    // específica y el pedido ya tiene productos de la subfamilia opuesta,
    // bloqueamos con alert antes de tocar nada.
    if (cantidadNum > 0 && clienteUsaSubfamilia(pedido?.cliente)) {
      const catNuevo = categoriaProducto(producto)
      if (catNuevo === 'cirugia' || catNuevo === 'medicina') {
        // Unimos las líneas que SIGUEN activas en el pedido (sin contar la
        // propia, que puede estar siendo actualizada) — descartamos las
        // eliminadas por el usuario.
        const lineasActuales = [
          ...articulos
            .filter(a => a.idarticulo !== producto.id
                      && a.id !== producto.id
                      && !articulosEliminados.has(a.id))
            .map(a => {
              const p = productosCompletos.find(pp => pp.id === a.idarticulo)
              return { familia: p?.familia, subfamilia: p?.subfamilia }
            }),
          ...Object.entries(lineasTemporales)
            .filter(([id]) => parseInt(id) !== producto.id)
            .map(([id]) => {
              const p = productosCompletos.find(pp => pp.id === parseInt(id))
              return { familia: p?.familia, subfamilia: p?.subfamilia }
            }),
        ]
        const conflicto = compatibilidadAlAnadirProducto(lineasActuales, producto)
        if (conflicto?.incompatible) {
          window.alert(conflicto.motivo)
          // Resetear la cantidad temporal para que el input no quede con valor inválido
          setCantidadesTemporales(prev => ({ ...prev, [producto.id]: '' }))
          return
        }
      }
    }

    // Verificar si el producto ya existe en líneas actuales o temporales
    const lineaExistente = articulos.find(art => art.idarticulo === producto.id || art.id === producto.id)
    const lineaTemporal = lineasTemporales[producto.id]

    if (lineaExistente && !articulosEliminados.has(lineaExistente.id)) {
      // Producto ya existe, modificar cantidad
      modificarCantidadArticulo(lineaExistente.id, cantidadNum)
    } else if (lineaTemporal) {
      // Ya existe línea temporal, actualizar cantidad
      const valorIva = producto.tipo_iva === 1 ? 21 : 
                       producto.tipo_iva === 2 ? 10 : 
                       producto.tipo_iva === 3 ? 4 : 21

      const lineaData = {
        ...lineaTemporal,
        cantidad: cantidadNum
      }

      setLineaTemporal(pedidoId, producto.id, lineaData)
      setLineasTemporales(prev => ({
        ...prev,
        [producto.id]: lineaData
      }))
      setCambiosPendientes(true)
    } else {
      // Producto nuevo, crear línea temporal
      agregarLineaTemporal(producto, cantidadNum)
    }
  }, [pedidoId, articulos, lineasTemporales, articulosEliminados, pedido, productosCompletos])

  // Función para eliminar producto (tanto temporal como existente)
  const eliminarProducto = useCallback((productId) => {
    // Verificar si existe como línea temporal
    if (lineasTemporales[productId]) {
      eliminarLineaTemporal(productId)
    }

    // Verificar si existe como artículo existente
    const articuloExistente = articulos.find(art => art.idarticulo === productId || art.id === productId)
    if (articuloExistente) {
      marcarArticuloEliminado(articuloExistente.id)
    }

    // Limpiar cantidad temporal
    setCantidadesTemporales(prev => {
      const newCantidades = { ...prev }
      delete newCantidades[productId]
      return newCantidades
    })
  }, [lineasTemporales, articulos])

  // Función para obtener cantidad que se debe mostrar en el input
  const getCantidadInput = (productoId) => {
    // Primero verificar si hay una cantidad temporal (usuario escribiendo)
    if (cantidadesTemporales[productoId] !== undefined) {
      return cantidadesTemporales[productoId]
    }

    // Verificar si existe en líneas temporales
    const lineaTemporal = lineasTemporales[productoId]
    if (lineaTemporal) {
      return lineaTemporal.cantidad
    }

    // Verificar si existe en artículos actuales
    const articuloExistente = articulos.find(art => art.idarticulo === productoId || art.id === productoId)
    if (articuloExistente && !articulosEliminados.has(articuloExistente.id)) {
      const cantidadModificada = articulosModificados[articuloExistente.id]?.cantidad
      return cantidadModificada !== undefined ? cantidadModificada : articuloExistente.cantidad
    }

    // Default: campo vacío
    return ""
  }

  // Función original agregarLineaTemporal (mejorada)
  const agregarLineaTemporal = (producto, cantidad) => {
    if (!cantidad || cantidad <= 0) return

    const valorIva = producto.tipo_iva === 1 ? 21 : 
                     producto.tipo_iva === 2 ? 10 : 
                     producto.tipo_iva === 3 ? 4 : 21

    const lineaData = {
      id: `temp_${producto.id}`,
      idarticulo: producto.id,
      nombre: producto.nombre,
      precio: producto.precio ?? producto.precio_coste ?? 0,
      iva: valorIva,
      cantidad: cantidad,
      ref_proveedor: producto.ref_proveedor,
      unidadbasica: producto.unidadbasica,
      temporal: true
    }

    // Actualizar sessionStorage
    setLineaTemporal(pedidoId, producto.id, lineaData)
    
    // Actualizar estado local
    setLineasTemporales(prev => ({
      ...prev,
      [producto.id]: lineaData
    }))

    setCambiosPendientes(true)
  }

  // Función para eliminar línea temporal
  const eliminarLineaTemporal = (productoId) => {
    removeLineaTemporal(pedidoId, productoId)
    setLineasTemporales(prev => {
      const newLineas = { ...prev }
      delete newLineas[productoId]
      return newLineas
    })
    setCambiosPendientes(true)
    
    // También limpiar la cantidad temporal
    setCantidadesTemporales(prev => {
      const newCantidades = { ...prev }
      delete newCantidades[productoId]
      return newCantidades
    })
  }

  // Función para marcar artículo existente como eliminado
  const marcarArticuloEliminado = (articuloId) => {
    setArticulosEliminados(prev => new Set([...prev, articuloId]))
    setCambiosPendientes(true)
  }

  // Función para modificar cantidad de artículo existente
  const modificarCantidadArticulo = (articuloId, nuevaCantidad) => {
    const articuloOriginal = articulos.find(a => a.id == articuloId)
    if (!articuloOriginal) return

    if (esUsuarioClinicaMatriz()) {
      // Solo cantidad para usuarios de clínica matriz
      setArticulosModificados(prev => ({
        ...prev,
        [articuloId]: {
          cantidad: nuevaCantidad,
          precio: articuloOriginal.precio, // Mantener precio original
          iva: articuloOriginal.iva // Mantener IVA original
        }
      }))
    } else {
      // Para administradores, mantener precio/IVA editados si existen
      const edicionesExistentes = articulosEditados[articuloId] || {}
      setArticulosModificados(prev => ({
        ...prev,
        [articuloId]: {
          cantidad: nuevaCantidad,
          precio: edicionesExistentes.precio !== undefined ? edicionesExistentes.precio : articuloOriginal.precio,
          iva: edicionesExistentes.iva !== undefined ? edicionesExistentes.iva : articuloOriginal.iva
        }
      }))
    }
    setCambiosPendientes(true)
  }

  // Combinar líneas existentes y temporales para mostrar (mejorado para evitar duplicados)
  const lineasCombinadas = useMemo(() => {
    const combinadas = []
    const productosYaAgregados = new Set()

    // Primero agregar artículos existentes (no eliminados)
    articulos.forEach(articulo => {
      if (!articulosEliminados.has(articulo.id)) {
        combinadas.push(articulo)
        productosYaAgregados.add(articulo.idarticulo || articulo.id)
      }
    })

    // Luego agregar líneas temporales que NO sean productos ya existentes
    Object.values(lineasTemporales).forEach(lineaTemporal => {
      if (!productosYaAgregados.has(lineaTemporal.idarticulo)) {
        combinadas.push(lineaTemporal)
      }
    })

    return combinadas
  }, [articulos, articulosEliminados, lineasTemporales])

  // Calcular totales combinados (mejorado)
  const totalCombinado = useMemo(() => {
    return lineasCombinadas.reduce((total, linea) => {
      let cantidad, precio, iva

      if (linea.temporal) {
        // Para líneas temporales
        cantidad = linea.cantidad
        precio = linea.precio
        iva = linea.iva
      } else {
        // Para líneas existentes, verificar si hay modificaciones
        const modificaciones = articulosModificados[linea.id]
        const ediciones = articulosEditados[linea.id]
        
        cantidad = modificaciones?.cantidad !== undefined ? modificaciones.cantidad : linea.cantidad
        precio = ediciones?.precio !== undefined ? ediciones.precio : linea.precio
        iva = ediciones?.iva !== undefined ? ediciones.iva : linea.iva
      }

      const subtotal = precio * cantidad
      const ivaValor = subtotal * (iva / 100)
      return total + subtotal + ivaValor
    }, 0)
  }, [lineasCombinadas, articulosModificados, articulosEditados])

  // Función para guardar todos los cambios (como finalizar en NuevoPedido.js)
  const handleGuardarCambios = async () => {
    if (!cambiosPendientes) {
      setMensaje({
        texto: "No hay cambios pendientes por guardar",
        tipo: "error",
      })
      return
    }

    try {
      setLoading(true)

      // 1. Eliminar artículos marcados para eliminación
      for (const articuloId of articulosEliminados) {
        await fetch(`${API_URL}/api/pedidos/${pedidoId}/articulos/${articuloId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      }

      // 2. Modificar artículos existentes (cantidades y precio/IVA si los editó el admin)
      for (const [articuloId, datos] of Object.entries(articulosModificados)) {
        const articuloOriginal = articulos.find(a => a.id == articuloId)
        if (articuloOriginal && !articulosEliminados.has(parseInt(articuloId))) {
          await fetch(`${API_URL}/api/pedidos/${pedidoId}/articulos/${articuloId}`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              precio: datos.precio,
              cantidad: datos.cantidad,
              iva: datos.iva,
            }),
          })
        }
      }

      // 2b. Guardar cambios de precio/IVA que hizo el admin (sin cambios de cantidad)
      for (const [articuloId, datos] of Object.entries(articulosEditados)) {
        // Solo procesar si hay cambios y no está ya procesado en articulosModificados
        if (!articulosModificados[articuloId]) {
          const articuloOriginal = articulos.find(a => a.id == articuloId)
          if (articuloOriginal && !articulosEliminados.has(parseInt(articuloId))) {
            // Verificar si realmente hay cambios
            const precioChanged = datos.precio !== articuloOriginal.precio
            const ivaChanged = datos.iva !== articuloOriginal.iva
            
            if (precioChanged || ivaChanged) {
              await fetch(`${API_URL}/api/pedidos/${pedidoId}/articulos/${articuloId}`, {
                method: "PUT",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  precio: datos.precio,
                  cantidad: articuloOriginal.cantidad, // Mantener cantidad original
                  iva: datos.iva,
                }),
              })
            }
          }
        }
      }

      // 3. Agregar nuevas líneas temporales
      for (const linea of Object.values(lineasTemporales)) {
        const respPost = await fetch(`${API_URL}/api/pedidos/${pedidoId}/articulos`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            producto_id: linea.idarticulo,
            cantidad: linea.cantidad,
          }),
        })
        // 409 incompatible_subfamilia → alert con motivo claro y abortar el guardado.
        // No tiramos throw genérico para que no caiga en el "Error al guardar cambios".
        if (!respPost.ok) {
          let body = null
          try { body = await respPost.json() } catch {}
          if (respPost.status === 409 && body?.error === 'incompatible_subfamilia') {
            window.alert(body.message || 'Este producto no es compatible con los productos actuales del pedido.')
            setLoading(false)
            return
          }
          throw new Error(body?.message || `Error al añadir el artículo (${respPost.status})`)
        }
      }

      // Limpiar sessionStorage y estados
      clearLineasTemporales(pedidoId)
      setLineasTemporales({})
      setArticulosModificados({})
      setArticulosEliminados(new Set())
      setCambiosPendientes(false)
      setCantidadesTemporales({})

      // Recargar datos del pedido
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setPedido(data.pedido)
        setArticulos(data.articulos)

        // Reinicializar artículos editados con nuevos valores
        const editadosInicial = {}
        data.articulos.forEach(articulo => {
          editadosInicial[articulo.id] = {
            cantidad: articulo.cantidad,
            precio: articulo.precio,
            iva: articulo.iva
          }
        })
        setArticulosEditados(editadosInicial)
      }

      setMensaje({
        texto: "Cambios guardados correctamente",
        tipo: "success",
      })

      setTimeout(() => {
        setMensaje({ texto: "", tipo: "" })
      }, 3000)

    } catch (error) {
      console.error("Error:", error)
      setMensaje({
        texto: "Error al guardar cambios: " + error.message,
        tipo: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  // Cargar datos del pedido
  useEffect(() => {
    const fetchPedido = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error("Error al obtener los detalles del pedido")
        }

        const data = await response.json()
        setPedido(data.pedido)
        setArticulos(data.articulos)
        
        // Inicializar los artículos recibidos
        const recibidos = data.articulos
          .filter(articulo => articulo.recibido === 1)
          .map(articulo => articulo.id.toString())
        setArticulosRecibidos(recibidos)

        // Inicializar estado de artículos editados
        const editadosInicial = {}
        data.articulos.forEach(articulo => {
          editadosInicial[articulo.id] = {
            cantidad: articulo.cantidad,
            precio: articulo.precio,
            iva: articulo.iva
          }
        })
        setArticulosEditados(editadosInicial)

        // Cargar líneas temporales desde sessionStorage
        const lineasTemp = getLineasTemporales(pedidoId)
        setLineasTemporales(lineasTemp)
        if (Object.keys(lineasTemp).length > 0) {
          setCambiosPendientes(true)
        }

        // Cargar productos disponibles del proveedor
        if (data.pedido.proveedor_id && (
          ![4, 5, 3].includes(user.permisos) || 
          (user.clinica_matriz > 0 && data.pedido.estado == 6)
        )) {
          // Cargar productos completos para la nueva funcionalidad
          cargarProductosCompletos(data.pedido.proveedor_id, data.pedido.cliente)
          
          // Mantener la carga original para compatibilidad
          const productosResponse = await fetch(
            `${API_URL}/api/productos/proveedor/${data.pedido.proveedor_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )

          if (productosResponse.ok) {
            const productosData = await productosResponse.json()
            setProductosDisponibles(productosData.productos)
          }
        }
      } catch (error) {
        console.error("Error:", error)
        setError("No se pudieron cargar los detalles del pedido. " + error.message)
      } finally {
        setLoading(false)
      }
    }

    fetchPedido()
  }, [pedidoId, token, user.permisos])


  // Descargar PDF del pedido
  const handleDownloadPdf = async () => {
    try {
      setLoadingPdf(true)
      
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}/pdf`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Error al generar el PDF")
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `pedido_${pedido.npedido || pedidoId}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

    } catch (error) {
      console.error("Error:", error)
      setMensaje({
        texto: "Error al descargar el PDF: " + error.message,
        tipo: "error",
      })
    } finally {
      setLoadingPdf(false)
    }
  }

  // No mostramos NADA (ni el header) hasta tener el pedido Y, si es editable,
  // los productos disponibles cargados. Así la pantalla aparece completa de una.
  if ((loading && !pedido) || (puedeModificarArticulos() && loadingProductos)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <div className="flex items-center space-x-4">
            <RefreshCw className="animate-spin text-custom-green" size={24} />
            <span className="text-lg font-medium text-gray-700">Cargando detalles del pedido...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4">
          <div className="flex items-center space-x-4 mb-4">
            <AlertTriangle className="text-red-500" size={24} />
            <span className="text-lg font-medium text-gray-700">Error</span>
          </div>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to={getBackRoute()}
            className="w-full bg-custom-green hover:bg-teal-600 text-white px-4 py-2 rounded-lg inline-flex items-center justify-center transition-colors"
          >
            <ArrowLeft size={16} className="mr-2" />
            {getBackButtonText()}
          </Link>
        </div>
      </div>
    )
  }

  const esEditable = puedeModificarArticulos() && productosCompletos.length > 0;

  return (
    <div className={esEditable ? "nuevo-pedido-root" : "min-h-screen bg-gray-50 py-4"}>

      {/* Header: barra superior (volver + título + info inline + PDF) */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              <Link
                to={getBackRoute()}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg inline-flex items-center text-sm transition-colors flex-shrink-0"
              >
                <ArrowLeft size={15} className="mr-1.5" />
                {getBackButtonText()}
              </Link>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900 truncate">
                    Pedido #{pedido?.npedido || pedidoId}
                  </h1>
                  {isViendoPedidoClinicaMatriz() && (
                    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}>
                      <Building2 size={12} className="mr-1" />
                      Clínica asociada
                    </span>
                  )}
                  {etiquetaTipoPedido(pedido?.tipo) && (
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-semibold ${
                      pedido.tipo === 'medicina_estetica' ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800'
                    }`}>
                      {etiquetaTipoPedido(pedido.tipo)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span><span className="text-gray-400">Cliente:</span> <strong className="text-gray-700">{pedido?.razon_social || '—'}</strong></span>
                  <span className="text-gray-300">·</span>
                  <span><span className="text-gray-400">Proveedor:</span> <strong className="text-gray-700">{pedido?.proveedor_nombre || '—'}</strong></span>
                  <span className="text-gray-300">·</span>
                  <span><span className="text-gray-400">Fecha:</span> <strong className="text-gray-700">{pedido?.fecha ? new Date(pedido.fecha).toLocaleDateString('es-ES') : 'N/A'}</strong></span>
                </div>
              </div>
            </div>

            <button
              onClick={handleDownloadPdf}
              disabled={loadingPdf}
              className="text-white px-4 py-2 rounded-lg inline-flex items-center text-sm transition-colors shadow flex-shrink-0 hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: theme.primary }}
            >
              {loadingPdf ? (
                <RefreshCw size={15} className="mr-2 animate-spin" />
              ) : (
                <Download size={15} className="mr-2" />
              )}
              {loadingPdf ? "Generando..." : "Descargar PDF"}
            </button>
          </div>
      </div>

      {/* Cuerpo: dos paneles anclados al viewport (estilo NuevoPedido) */}
      {esEditable && (
        <div className="nuevo-pedido-body">

          {/* PANEL IZQUIERDO: Productos disponibles */}
          <div className="nuevo-pedido-left">
            <div className="panel-section">

              <div className="flex items-center gap-2 text-xs mb-2" style={{ color: theme.primaryDark }}>
                <AlertTriangle size={13} />
                <span>Escribe la cantidad y pulsa <strong>Enter</strong> (o sal del campo) para añadir/modificar.</span>
              </div>
              <input
                type="text"
                placeholder="Buscar por nombre o referencia..."
                value={busquedaProducto}
                onChange={(e) => setBusquedaProducto(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm"
              />
            </div>

            {/* Catálogo de productos (scroll interno) */}
            <div className="productos-scroll-area">
                  {loadingProductos ? (
                    <div className="flex justify-center items-center h-full">
                      <RefreshCw className="animate-spin text-custom-green mr-2" size={24} />
                      <span className="text-sm text-gray-700">Cargando...</span>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-custom-green text-white">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Ref</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Producto</th>
                          {aplicaTipos && <th className="px-2 py-2 text-center text-xs font-semibold">Tipo</th>}
                          <th className="px-2 py-2 text-center text-xs font-semibold">Precio</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold">IVA</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold">Cant</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productosFiltrados.map((producto, index) => (
                          <tr 
                            key={producto.id}
                            className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                          >
                            
                            {/* Referencia */}
                            <td className="px-2 py-2">
                              <TruncatedReference 
                                referencia={producto.ref_proveedor} 
                                maxLength={8} 
                              />
                            </td>
                            
                            {/* Nombre del producto */}
                            <td className="px-2 py-2">
                              <div className="font-medium text-gray-900 text-xs">{producto.nombre}</div>
                              {producto.comentarios && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {producto.comentarios.substring(0, 30)}{producto.comentarios.length > 30 ? '...' : ''}
                                </div>
                              )}
                            </td>

                            {/* Tipo de producto (Sanitario / Oficina / Limpieza) */}
                            {aplicaTipos && (
                              <td className="px-2 py-2 text-center">
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                                  {etiquetaTipoProducto(producto.familia)}
                                </span>
                              </td>
                            )}

                            {/* Precio */}
                            <td className="px-2 py-2 text-center">
                              <span className="font-medium text-xs">
                                {formatCurrency(producto.precio ?? producto.precio_coste ?? 0)}
                              </span>
                            </td>
                            
                            {/* IVA */}
                            <td className="px-2 py-2 text-center">
                              <span className="text-xs bg-gray-100 px-1 py-1 rounded">
                                {producto.tipo_iva === 1 ? '21%' : 
                                 producto.tipo_iva === 2 ? '10%' : 
                                 producto.tipo_iva === 3 ? '4%' : '21%'}
                              </span>
                            </td>
                            
                            {/* Cantidad */}
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={getCantidadInput(producto.id)}
                                onChange={(e) => handleCantidadChange(producto.id, e.target.value)}
                                onBlur={(e) => confirmarCantidad(producto, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur() // Trigger onBlur
                                  }
                                }}
                                step="0.0001"
                                min="0"
                                placeholder="0"
                                className="w-16 p-1 border border-gray-300 rounded text-center focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors text-xs"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  
                  {!loadingProductos && productosFiltrados.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Package size={30} className="mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">No se encontraron productos</p>
                    </div>
                  )}
                </div>
              </div>

          {/* PANEL DERECHO: Líneas del pedido */}
          <div className="nuevo-pedido-right">
            <div className="panel-section flex justify-between items-center">
              <h2 className="text-base font-bold text-gray-900 flex items-center">
                <ShoppingCart size={18} className="mr-2 text-custom-green" />
                Líneas del Pedido ({lineasCombinadas.length})
              </h2>
              <button
                onClick={handleGuardarCambios}
                disabled={loading || !cambiosPendientes}
                className="bg-custom-green text-white px-4 py-2 rounded-lg inline-flex items-center transition-colors shadow font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:bg-teal-600"
                title={!cambiosPendientes ? "No hay cambios para guardar" : ""}
              >
                {loading ? (
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                ) : (
                  <Save size={14} className="mr-2" />
                )}
                {loading ? "Guardando..." : "Guardar Cambios"}
              </button>
            </div>

            {/* Tabla de líneas (scroll interno) */}
            <div className="detalle-scroll-area">
                  {lineasCombinadas.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-100">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700">Producto</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Cant</th>
                          {!esUsuarioClinicaMatriz() && (
                            <>
                              <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Precio</th>
                              <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">IVA</th>
                            </>
                          )}
                          <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700">Total</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Acc</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineasCombinadas.map((linea, index) => {
                          const esLineaTemporal = linea.temporal
                          
                          // Obtener cantidad correcta
                          let cantidad
                          if (esLineaTemporal) {
                            // Para líneas temporales, verificar si hay cantidad temporal en edición
                            cantidad = cantidadesTemporales[linea.idarticulo] !== undefined 
                              ? cantidadesTemporales[linea.idarticulo] 
                              : linea.cantidad
                          } else {
                            // Para líneas existentes, usar modificaciones o cantidad original
                            cantidad = articulosModificados[linea.id]?.cantidad !== undefined
                              ? articulosModificados[linea.id].cantidad
                              : linea.cantidad
                          }

                          // Obtener precio e IVA correctos (incluyendo ediciones de admin)
                          let precio, iva
                          if (esLineaTemporal) {
                            precio = linea.precio
                            iva = linea.iva
                          } else {
                            // Para líneas existentes, usar ediciones o valores originales
                            precio = articulosEditados[linea.id]?.precio !== undefined 
                              ? articulosEditados[linea.id].precio 
                              : linea.precio
                            iva = articulosEditados[linea.id]?.iva !== undefined 
                              ? articulosEditados[linea.id].iva 
                              : linea.iva
                          }

                          const subtotal = precio * (parseFloat(cantidad) || 0)
                          const ivaValor = subtotal * (iva / 100)
                          const total = subtotal + ivaValor
                          
                          return (
                            <tr 
                              key={linea.id}
                              className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                                esLineaTemporal ? 'bg-blue-50' : 
                                index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                              }`}
                            >
                              {/* Producto */}
                              <td className="px-2 py-2">
                                <div className="font-medium text-gray-900 text-xs">
                                  {linea.nombre}
                                  {esLineaTemporal && (
                                    <span className="ml-1 text-xs text-blue-600">(Nuevo)</span>
                                  )}
                                </div>
                                {!esLineaTemporal && linea.cantidad_faltante > 0 && (
                                  <div className="mt-1">
                                    <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                                      Falta {linea.cantidad_faltante} de {linea.cantidad}
                                      {linea.cantidad_faltante >= linea.cantidad ? ' (línea entera)' : ''}
                                    </span>
                                  </div>
                                )}
                              </td>
                              
                              {/* Cantidad */}
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  value={cantidad}
                                  onChange={(e) => {
                                    const nuevaCantidad = e.target.value
                                    if (esLineaTemporal) {
                                      // Para líneas temporales, actualizar directamente
                                      setCantidadesTemporales(prev => ({
                                        ...prev,
                                        [linea.idarticulo]: nuevaCantidad
                                      }))
                                    } else {
                                      // Para líneas existentes, usar el estado de modificación
                                      const articuloOriginal = articulos.find(a => a.id == linea.id)
                                      if (articuloOriginal) {
                                        setArticulosModificados(prev => ({
                                          ...prev,
                                          [linea.id]: {
                                            cantidad: parseFloat(nuevaCantidad) || 0,
                                            precio: esUsuarioClinicaMatriz() ? articuloOriginal.precio : (articulosEditados[linea.id]?.precio || articuloOriginal.precio),
                                            iva: esUsuarioClinicaMatriz() ? articuloOriginal.iva : (articulosEditados[linea.id]?.iva || articuloOriginal.iva)
                                          }
                                        }))
                                        setCambiosPendientes(true)
                                      }
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const nuevaCantidad = parseFloat(e.target.value) || 0
                                    if (esLineaTemporal) {
                                      if (nuevaCantidad > 0) {
                                        const productoOriginal = productosCompletos.find(p => p.id == linea.idarticulo)
                                        if (productoOriginal) {
                                          agregarLineaTemporal(productoOriginal, nuevaCantidad)
                                        }
                                      } else {
                                        eliminarLineaTemporal(linea.idarticulo)
                                      }
                                    }
                                    // Para líneas existentes, el onChange ya maneja la actualización
                                  }}
                                  step="0.0001"
                                  min="0"
                                  className="w-16 p-1 border border-gray-300 rounded text-center focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors text-xs"
                                />
                              </td>
                              {/* Precio y IVA editables para administradores, solo lectura para clínica matriz */}
                              {!esUsuarioClinicaMatriz() && (
                                <>
                                  {/* Precio - Editable para administradores */}
                                  <td className="px-2 py-2 text-center">
                                    {esLineaTemporal ? (
                                      <span className="text-xs">{formatCurrency(precio)}</span>
                                    ) : (
                                      <input
                                        type="number"
                                        value={articulosEditados[linea.id]?.precio || precio}
                                        onChange={(e) => {
                                          const nuevoPrecio = parseFloat(e.target.value) || 0
                                          setArticulosEditados(prev => ({
                                            ...prev,
                                            [linea.id]: {
                                              ...prev[linea.id],
                                              precio: nuevoPrecio
                                            }
                                          }))
                                          setCambiosPendientes(true)
                                        }}
                                        step="0.01"
                                        min="0"
                                        className="w-16 p-1 border border-gray-300 rounded text-center focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors text-xs"
                                        title="Precio editable (solo administradores)"
                                      />
                                    )}
                                  </td>
                                  
                                  {/* IVA - Editable para administradores */}
                                  <td className="px-2 py-2 text-center">
                                    {esLineaTemporal ? (
                                      <span className="text-xs">{iva}%</span>
                                    ) : (
                                      <select
                                        value={articulosEditados[linea.id]?.iva || iva}
                                        onChange={(e) => {
                                          const nuevoIva = parseFloat(e.target.value)
                                          setArticulosEditados(prev => ({
                                            ...prev,
                                            [linea.id]: {
                                              ...prev[linea.id],
                                              iva: nuevoIva
                                            }
                                          }))
                                          setCambiosPendientes(true)
                                        }}
                                        className="w-14 p-1 border border-gray-300 rounded text-center focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors text-xs"
                                        title="IVA editable (solo administradores)"
                                      >
                                        <option value={4}>4%</option>
                                        <option value={10}>10%</option>
                                        <option value={21}>21%</option>
                                      </select>
                                    )}
                                  </td>
                                </>
                              )}
                              
                              {/* Total */}
                              <td className="px-2 py-2 text-right">
                                <span className="font-medium text-xs">{formatCurrency(total)}</span>
                              </td>
                              
                              {/* Acciones */}
                              <td className="px-2 py-2 text-center">
                                <button
                                  onClick={() => {
                                    if (esLineaTemporal) {
                                      eliminarLineaTemporal(linea.idarticulo)
                                    } else {
                                      marcarArticuloEliminado(linea.id)
                                    }
                                  }}
                                  className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                                  title="Eliminar"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-100 border-t-2 border-gray-300">
                          <td colSpan={esUsuarioClinicaMatriz() ? 3 : 5} className="px-2 py-2 text-right font-semibold text-xs text-gray-700">
                            TOTAL:
                          </td>
                          <td className="px-2 py-2 text-right font-bold text-sm text-teal-600">
                            {formatCurrency(totalCombinado)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                    <div className="text-center py-8 text-gray-500 h-full flex items-center justify-center">
                      <div>
                        <AlertTriangle size={30} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">No hay artículos en este pedido</p>
                        <p className="text-xs">Selecciona productos de la izquierda</p>
                      </div>
                    </div>
                  )}
                </div>

            {/* Footer: aviso de cambios pendientes */}
            {cambiosPendientes && (
              <div className="detalle-footer">
                <div className="flex items-center text-yellow-800 text-sm bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                  <AlertTriangle size={14} className="mr-2" />
                  <span className="font-medium">Tienes cambios pendientes. Pulsa «Guardar Cambios» para aplicarlos.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Vista read-only de líneas para proveedor (permisos=4) */}
      {user?.permisos === 4 && articulos.length > 0 && (
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <ShoppingCart size={20} className="mr-2 text-custom-green" />
              Líneas del Pedido ({articulos.length})
            </h2>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Producto</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Cantidad</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Estado envío</th>
                  </tr>
                </thead>
                <tbody>
                  {articulos.map((linea, index) => (
                    <tr
                      key={linea.id}
                      className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900 text-xs">{linea.nombre}</div>
                        {linea.ref_proveedor && (
                          <div className="text-[10px] text-gray-500">Ref: {linea.ref_proveedor}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-800">{linea.cantidad}</td>
                      <td className="px-3 py-2 text-center">
                        {linea.cantidad_faltante > 0 ? (
                          <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                            Falta {linea.cantidad_faltante} de {linea.cantidad}
                            {linea.cantidad_faltante >= linea.cantidad ? ' (línea entera)' : ''}
                          </span>
                        ) : (
                          <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                            Enviado
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-500 italic">
              Esta vista es solo de lectura. Para modificar cantidades faltantes, usá el botón "Envío parcial" desde la gestión de pedidos.
            </p>
          </div>
        </div>
      )}

      {/* Modal para mostrar imagen en grande */}
      <ImageModal
        isOpen={modalImagenVisible}
        imageSrc={imagenSeleccionada.src}
        imageAlt={imagenSeleccionada.alt}
        onClose={handleCloseImageModal}
      />
    </div>
  )
}

export default DetallePedido